/**
 * Core-100 R4 — CORE-BE: "Capacity implications SHALL be visible before the boundary, so that tomorrow is
 * not silently over-committed by today's leftovers."
 *
 * Pure. Given the technician, their booked work on the coming days and the minutes about to carry, answer for
 * the technician's NEXT WORKING DAY: how many minutes are scheduled (CORE-AW `resolveTechnicianCapacity`, basis
 * 'scheduled' — the booked figure is OptiTime/wall minutes, which already carry efficiency and buffer), how many
 * are already booked, and what the carry would make it. Unknown stays unknown: a day whose hours are not known
 * is said to be unknown rather than shown as room.
 */
import { addDays, format } from 'date-fns';
import { resolveTechnicianCapacity } from '@/utils/capacity/technicianCapacityState';
// DMC-D3c-1: canonical deleted-state helper.
import { isTicketDeleted } from '@/utils/ticketDeletedState';

export interface CarryCapacityTicket {
  id: string;
  assigned_tech_id: string | null;
  scheduled_date: string | null;
  status?: string | null;
  deleted_at?: string | null;
  completed_at?: string | null;
  opti_time?: number | null;
  predicted_minutes?: number | null;
}

export type CarryCapacityPreview =
  | { state: 'known'; date: string; capacityMinutes: number; bookedMinutes: number; afterCarryMinutes: number; overByMinutes: number }
  | { state: 'unknown'; date: string }
  | { state: 'no_working_day'; searchedDays: number };

/** Minutes a ticket holds on its day: its OptiTime, or its book minutes when no OptiTime is recorded. */
export const bookedMinutesOf = (t: CarryCapacityTicket): number =>
  Math.max(0, Math.round(Number(t.opti_time ?? t.predicted_minutes ?? 0) || 0));

export function previewCarryCapacity(input: {
  tech: Record<string, unknown> & { id: string };
  tickets: ReadonlyArray<CarryCapacityTicket>;
  /** The shop-local day the work is carried FROM (YYYY-MM-DD). */
  fromDate: string;
  carryMinutes: number;
  /** The ticket being carried (excluded from tomorrow's booked total if it is already there). */
  ticketId?: string;
  searchDays?: number;
}): CarryCapacityPreview {
  const searchDays = input.searchDays ?? 14;
  const [y, m, d] = input.fromDate.split('-').map(Number);
  const from = new Date(y, m - 1, d);
  for (let i = 1; i <= searchDays; i++) {
    const day = addDays(from, i);
    const date = format(day, 'yyyy-MM-dd');
    const cap = resolveTechnicianCapacity(input.tech, day, 'scheduled');
    if (cap.state === 'unknown') return { state: 'unknown', date };
    if (cap.state === 'known_zero' || cap.minutes <= 0) continue;
    const booked = input.tickets
      .filter((t) => t.assigned_tech_id === input.tech.id && t.scheduled_date === date && t.id !== input.ticketId
        && !isTicketDeleted(t) && !t.completed_at && t.status !== 'Completed' && t.status !== 'Cancelled')
      .reduce((sum, t) => sum + bookedMinutesOf(t), 0);
    const after = booked + Math.max(0, Math.round(input.carryMinutes));
    return { state: 'known', date, capacityMinutes: cap.minutes, bookedMinutes: booked, afterCarryMinutes: after, overByMinutes: Math.max(0, after - cap.minutes) };
  }
  return { state: 'no_working_day', searchedDays: searchDays };
}

export function describeCarryCapacity(p: CarryCapacityPreview, techName: string): string {
  if (p.state === 'no_working_day') return `${techName} has no working day in the next ${p.searchedDays} days — the carried work has nowhere to land.`;
  const day = format(new Date(`${p.date}T12:00:00`), 'EEE MMM d');
  if (p.state === 'unknown') return `${techName}'s hours on ${day} are not known, so whether this carry fits cannot be said.`;
  const base = `${day}: ${techName} has ${p.capacityMinutes} min scheduled, ${p.bookedMinutes} already booked; with this carry ${p.afterCarryMinutes}.`;
  return p.overByMinutes > 0 ? `${base} That is ${p.overByMinutes} min more than the day holds.` : base;
}
